import {
  FINANCIAL_WORK_NAV_PARAM,
  FINANCIAL_WORK_OBJECT_ID_PARAMS,
  FINANCIAL_WORK_OBJECT_PARAM,
  FINANCIAL_WORK_REV_ID_PARAM,
  FINANCIAL_WORK_REV_PARAM,
  isFinancialWorkNavId,
  kindFromObjectIdParam,
  objectIdParamForKind,
} from "./nav";
import type { FinancialWorkIntent, FinancialWorkNavId, ObjectRef } from "./types";

const INTENT_KEY = "aera_financial_work_intent_v1";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function buildObjectRef(kind: string, id: string): ObjectRef {
  return { kind: kind.trim(), id: id.trim() };
}

export function encodeObjectRef(ref: ObjectRef): string {
  return `${encodeURIComponent(ref.kind)}:${encodeURIComponent(ref.id)}`;
}

export function parseObjectRef(raw: string | null | undefined): ObjectRef | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const idx = s.indexOf(":");
  if (idx <= 0) return null;
  const kind = decodeURIComponent(s.slice(0, idx)).trim();
  const id = decodeURIComponent(s.slice(idx + 1)).trim();
  if (!kind || !id) return null;
  return { kind, id };
}

type SearchParamReader = { get(name: string): string | null };

/** `object=receivable_batch_id` + `receivable_batch_id=<id>`; packed `object=kind:id` still parses. */
export function parseObjectRefFromSearch(
  params: SearchParamReader,
): ObjectRef | null {
  const objectVal = params.get(FINANCIAL_WORK_OBJECT_PARAM)?.trim() || "";
  if (objectVal && !objectVal.includes(":")) {
    const kind = kindFromObjectIdParam(objectVal);
    const id = params.get(objectVal)?.trim() || "";
    if (kind && id) return { kind, id };
  }
  const packed = parseObjectRef(objectVal);
  if (packed) return packed;
  for (const [kind, key] of Object.entries(FINANCIAL_WORK_OBJECT_ID_PARAMS)) {
    const id = params.get(key)?.trim() || "";
    if (id) return { kind, id };
  }
  return null;
}

export function applyNamedObjectQuery(
  params: URLSearchParams,
  ref: ObjectRef | null,
  revision?: number | null,
): void {
  params.delete(FINANCIAL_WORK_OBJECT_PARAM);
  params.delete(FINANCIAL_WORK_REV_PARAM);
  params.delete(FINANCIAL_WORK_REV_ID_PARAM);
  for (const key of Object.values(FINANCIAL_WORK_OBJECT_ID_PARAMS)) {
    params.delete(key);
  }
  if (!ref) return;
  const idKey = objectIdParamForKind(ref.kind);
  params.set(FINANCIAL_WORK_OBJECT_PARAM, idKey);
  params.set(idKey, ref.id);
  params.set(FINANCIAL_WORK_REV_PARAM, FINANCIAL_WORK_REV_ID_PARAM);
  if (revision != null && Number.isFinite(revision)) {
    params.set(FINANCIAL_WORK_REV_ID_PARAM, String(revision));
  }
}

/** Patch for `buildFinancialWorkHref`: `object=receivable_batch_id` + actual id param. */
export function financialWorkObjectHrefPatch(
  ref: ObjectRef | null,
  revision?: number | null,
): Record<string, string | null> {
  const patch: Record<string, string | null> = {
    [FINANCIAL_WORK_OBJECT_PARAM]: null,
    [FINANCIAL_WORK_REV_PARAM]: null,
    [FINANCIAL_WORK_REV_ID_PARAM]: null,
  };
  for (const key of Object.values(FINANCIAL_WORK_OBJECT_ID_PARAMS)) {
    patch[key] = null;
  }
  if (!ref) return patch;
  const idKey = objectIdParamForKind(ref.kind);
  patch[FINANCIAL_WORK_OBJECT_PARAM] = idKey;
  patch[idKey] = ref.id;
  patch[FINANCIAL_WORK_REV_PARAM] = FINANCIAL_WORK_REV_ID_PARAM;
  if (revision != null && Number.isFinite(revision)) {
    patch[FINANCIAL_WORK_REV_ID_PARAM] = String(revision);
  }
  return patch;
}

/** Persist opaque work intent across LIFF login/binding interruption. */
export function saveFinancialWorkIntent(
  intent: Omit<FinancialWorkIntent, "v" | "savedAt"> & {
    savedAt?: string;
  },
): void {
  if (!isBrowser()) return;
  const payload: FinancialWorkIntent = {
    v: 1,
    objectRef: intent.objectRef,
    caseRef: intent.caseRef,
    desiredAction: intent.desiredAction,
    nav: intent.nav,
    returnPath: intent.returnPath,
    savedAt: intent.savedAt ?? new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function readFinancialWorkIntent(): FinancialWorkIntent | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FinancialWorkIntent;
    if (parsed?.v !== 1 || typeof parsed.returnPath !== "string") return null;
    if (!parsed.returnPath.startsWith("/") || parsed.returnPath.startsWith("//")) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearFinancialWorkIntent(): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.removeItem(INTENT_KEY);
  } catch {
    // ignore
  }
}

/**
 * After login/binding, restore the opaque intent path once.
 * Returns the path to navigate, or null if nothing pending.
 */
export function consumeFinancialWorkIntentPath(): string | null {
  const intent = readFinancialWorkIntent();
  if (!intent) return null;
  clearFinancialWorkIntent();
  return intent.returnPath;
}

/** True when the URL looks like an open Financial Work detail (or work landing with object). */
export function locationHasFinancialWorkIntent(
  pathname: string,
  search: string,
): boolean {
  if (!pathname.includes("/service/")) return false;
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  return Boolean(parseObjectRefFromSearch(params));
}

/**
 * Persist current Financial Work object across login/binding interruption.
 * Safe no-op when the URL is not a financial-work detail deep link.
 */
const OPEN_RECORD_KEY = "aera_financial_work_open_record_v1";

export type FinancialWorkOpenRecord = {
  workerId: string;
  recordId: string;
  highlight?: string;
};

/** Remember which ข้อมูล row to open after switching off งานเงิน (URL can lag). */
export function saveFinancialWorkOpenRecord(nav: FinancialWorkOpenRecord): void {
  if (!isBrowser()) return;
  const recordId = nav.recordId.trim();
  const workerId = nav.workerId.trim();
  if (!recordId || !workerId) return;
  try {
    sessionStorage.setItem(
      OPEN_RECORD_KEY,
      JSON.stringify({
        v: 1,
        workerId,
        recordId,
        highlight: (nav.highlight || recordId).trim(),
        savedAt: Date.now(),
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function consumeFinancialWorkOpenRecord(): FinancialWorkOpenRecord | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(OPEN_RECORD_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(OPEN_RECORD_KEY);
    const parsed = JSON.parse(raw) as FinancialWorkOpenRecord & { v?: number };
    const recordId = String(parsed.recordId ?? "").trim();
    const workerId = String(parsed.workerId ?? "").trim();
    if (!recordId || !workerId) return null;
    return {
      workerId,
      recordId,
      highlight: String(parsed.highlight ?? recordId).trim() || recordId,
    };
  } catch {
    return null;
  }
}

export function captureFinancialWorkIntentFromLocation(
  href?: string,
): boolean {
  if (!isBrowser()) return false;
  try {
    const url = new URL(href ?? window.location.href, window.location.origin);
    if (!locationHasFinancialWorkIntent(url.pathname, url.search)) return false;

    const params = url.searchParams;
    const objectRef = parseObjectRefFromSearch(params);
    if (!objectRef) return false;

    const fwRaw = params.get(FINANCIAL_WORK_NAV_PARAM);
    const nav: FinancialWorkNavId | undefined = isFinancialWorkNavId(fwRaw)
      ? fwRaw
      : "work";

    saveFinancialWorkIntent({
      objectRef,
      nav,
      returnPath: `${url.pathname}${url.search}`,
    });
    return true;
  } catch {
    return false;
  }
}
